import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface VerifiedBadgeProps {
  type: 'admin' | 'premium' | 'partner';
  size?: number; // px
  className?: string;
  title?: string;
  shadow?: boolean; // apply drop shadow for readability
}

// Simple helper to compute a public URL for the asset.
function getBadgeUrl(fileName: string): string {
  try {
    const { data } = supabase.storage.from('fomo-assets').getPublicUrl(fileName);
    return data.publicUrl;
  } catch {
    return '';
  }
}

const VerifiedBadge: React.FC<VerifiedBadgeProps> = ({ type, size = 14, className, title, shadow = true }) => {
  const [scrollShimmer, setScrollShimmer] = useState(0);
  const [mouseShimmer, setMouseShimmer] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [maskSupported, setMaskSupported] = useState(true);
  const badgeRef = useRef<HTMLSpanElement>(null);

  const { src, alt, tooltip, gradient, shimmerIntensity } = useMemo(() => {
    const baseSrc = getBadgeUrl('fomo_badge.png');
    
    if (type === 'admin') {
      return {
        src: baseSrc,
        alt: 'FOMO Admin',
        tooltip: 'Verified Admin',
        gradient: 'linear-gradient(135deg, #FFD700, #FFA500, #FF8C00, #FFD700)',
        shimmerIntensity: 15 // Subtle gold shimmer
      };
    }
    if (type === 'partner') {
      return {
        src: baseSrc,
        alt: 'FOMO Partner',
        tooltip: 'Verified Partner',
        gradient: 'linear-gradient(135deg, #00D4FF, #7B68EE, #9370DB, #00D4FF)',
        shimmerIntensity: 12 // Subtle cyan/purple shimmer
      };
    }
    // Premium - minimal shimmer
    return {
      src: baseSrc,
      alt: 'Premium Member',
      tooltip: 'Premium Member',
      gradient: 'linear-gradient(135deg, #9b87f5, #7E69AB)',
      shimmerIntensity: 8
    };
  }, [type]);

  // Subtle iridescent shimmer from scroll - like tilting a holographic sticker
  useEffect(() => {
    const handleScroll = () => {
      if (badgeRef.current) {
        const rect = badgeRef.current.getBoundingClientRect();
        const viewportCenter = window.innerHeight / 2;
        const badgeCenter = rect.top + rect.height / 2;
        
        // Create a smooth sine wave shimmer based on position
        const distanceFromCenter = (badgeCenter - viewportCenter) / window.innerHeight;
        const shimmerValue = Math.sin(distanceFromCenter * Math.PI * 2) * shimmerIntensity;
        setScrollShimmer(shimmerValue);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial calculation
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [shimmerIntensity]);

  // Mouse position shimmer - makes it feel like you're tilting the badge
  // Works within a radius, not just on hover for that special feel ✨
  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!badgeRef.current) return;
    
    const rect = badgeRef.current.getBoundingClientRect();
    
    // Quick check: is badge even visible in viewport?
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      if (mouseShimmer !== 0) setMouseShimmer(0);
      return;
    }
    
    const badgeCenterX = rect.left + rect.width / 2;
    const badgeCenterY = rect.top + rect.height / 2;
    
    // Calculate angle from badge center to mouse
    const deltaX = e.clientX - badgeCenterX;
    const deltaY = e.clientY - badgeCenterY;
    
    // Calculate distance (fast: check squared distance first to avoid sqrt when far)
    const distanceSquared = deltaX * deltaX + deltaY * deltaY;
    const maxDistance = 500; // Mouse influence radius in pixels
    const maxDistanceSquared = maxDistance * maxDistance;
    
    // Only shimmer if within radius
    if (distanceSquared < maxDistanceSquared) {
      const distance = Math.sqrt(distanceSquared);
      // Smooth falloff: stronger when closer
      const influence = 1 - distance / maxDistance;
      
      // Calculate shimmer based on angle and proximity
      const angle = Math.atan2(deltaY, deltaX);
      const shimmerValue = Math.sin(angle * 2) * shimmerIntensity * influence;
      
      setMouseShimmer(shimmerValue);
    } else if (mouseShimmer !== 0) {
      // Fade out when mouse leaves radius (only update if needed)
      setMouseShimmer(0);
    }
  }, [shimmerIntensity, mouseShimmer]);

  useEffect(() => {
    // Always listen for mouse moves (performance optimized with passive listener)
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  if (!src) return null;

  // Detect CSS mask support (fallback to plain image if unsupported)
  useEffect(() => {
    try {
      const ok = typeof (window as any) !== 'undefined'
        && typeof (window as any).CSS !== 'undefined'
        && (
          (window as any).CSS.supports('mask-image', `url(${src})`)
          || (window as any).CSS.supports('-webkit-mask-image', `url(${src})`)
        );
      setMaskSupported(!!ok);
    } catch {
      setMaskSupported(false);
    }
  }, [src]);

  // Combine scroll and mouse shimmer for layered effect
  const totalShimmer = scrollShimmer + (mouseShimmer * 0.7); // Mouse shimmer is 70% strength
  const hueRotate = totalShimmer; // Only ±8-15 degrees combined
  const brightness = isHovered ? 1.2 : 1 + (Math.abs(totalShimmer) / 100);
  const saturate = isHovered ? 1.3 : 1 + (Math.abs(totalShimmer) / 80);

  const filterEffect = `hue-rotate(${hueRotate}deg) brightness(${brightness}) saturate(${saturate})`;

  const shadowFilter = shadow 
    ? 'drop-shadow(0 0 0.5px rgba(0,0,0,0.9)) drop-shadow(0 0 2px rgba(0,0,0,0.55))'
    : '';

  const combinedFilter = `${shadowFilter} ${filterEffect}`;

  // Plain image fallback (no mask)
  if (!maskSupported) {
    return (
      <span
        ref={badgeRef as React.MutableRefObject<HTMLSpanElement>}
        className={className || 'inline-block align-middle'}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        style={{
          width: size,
          height: size,
          position: 'relative',
          cursor: 'pointer',
          transition: 'filter 0.3s ease-out',
          filter: `${shadowFilter} ${filterEffect}`,
        }}
        title={title || tooltip}
      >
        <img
          src={src}
          alt={alt}
          style={{ width: '100%', height: '100%', display: 'block', userSelect: 'none', pointerEvents: 'none' }}
        />
      </span>
    );
  }

  return (
    <span
      ref={badgeRef as React.MutableRefObject<HTMLSpanElement>}
      className={className || 'inline-block align-middle'}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        width: size,
        height: size,
        position: 'relative',
        cursor: 'pointer',
        transition: 'filter 0.3s ease-out', // Smooth shimmer transition
      }}
      title={title || tooltip}
    >
      {/* Base badge image with gradient overlay */}
      <span
        style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          display: 'block',
          filter: combinedFilter,
        }}
      >
        <img
          src={src}
          alt={alt}
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            userSelect: 'none',
            pointerEvents: 'none',
            opacity: 0,
          }}
        />
        {/* Base gradient layer with mask */}
        <span
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            display: 'block',
            background: gradient,
            WebkitMaskImage: `url(${src})`,
            WebkitMaskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskImage: `url(${src})`,
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
            pointerEvents: 'none',
          }}
        />
        {/* Reflective shine overlay - creates depth and metallic look */}
        <span
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            display: 'block',
            background: 'radial-gradient(ellipse at 30% 20%, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.2) 25%, transparent 50%)',
            WebkitMaskImage: `url(${src})`,
            WebkitMaskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskImage: `url(${src})`,
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
            pointerEvents: 'none',
            mixBlendMode: 'soft-light',
            opacity: 0.4,
          }}
        />
        {/* Darker edge shadow for depth */}
        <span
          style={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            display: 'block',
            background: 'radial-gradient(ellipse at 50% 50%, transparent 50%, rgba(0, 0, 0, 0.2) 100%)',
            WebkitMaskImage: `url(${src})`,
            WebkitMaskSize: 'contain',
            WebkitMaskRepeat: 'no-repeat',
            WebkitMaskPosition: 'center',
            maskImage: `url(${src})`,
            maskSize: 'contain',
            maskRepeat: 'no-repeat',
            maskPosition: 'center',
            pointerEvents: 'none',
            mixBlendMode: 'multiply',
            opacity: 0.25,
          }}
        />
      </span>
    </span>
  );
};

export default VerifiedBadge;
